/**
 * Drag-and-drop onto the canvas wrapper. Two co-existing channels:
 *
 *   1. Native HTML5 drops — OS files (from the desktop) and widget-preset
 *      drags (from the in-app widget picker which uses native HTML5 DnD).
 *   2. dnd-kit drops — explorer file rows / folders dragged via the
 *      unified `CanvasDndProvider`. The board registers itself as a
 *      droppable (`canvas-board:<canvasId>`) and a board-drop handler
 *      with the provider; the dispatcher invokes it with cursor
 *      coordinates resolved from `activatorEvent + delta`.
 *
 * The two channels are mutually exclusive at the browser layer:
 * dnd-kit pointer drags don't populate `dataTransfer`, so the native
 * handlers' `Files` / `application/widget-preset` guards filter them
 * out automatically.
 */

import {
  runContainerFileDropWorkflow,
  runCrossWorkspaceFileDropWorkflow,
  runWorkspaceAddModeWorkflow,
} from "@/action-dialog";
import { useCanvasById } from "@/api/canvas";
import { fileRecordKeys, useCopyFileRecord } from "@/api/fileRecords";
import type { ResolvedCopyOperation } from "@/components/FileConflictResolver";
import { useFileConflictResolver } from "@/components/FileConflictResolver";
import {
  WORKSPACE_FILE_DRAG_MIME,
  WORKSPACE_FOLDER_DRAG_MIME,
  type WorkspaceFolderDragPayload,
} from "@/constants/workspaceFileDrag";
import { droppableId } from "@/dnd/ids";
import {
  buildCopyPayloadFromRecord,
  type ExplorerFileRecordCopyPayload,
} from "@/dnd/payloads";
import {
  useRegisterCanvasBoardDropHandler,
  type CanvasBoardDropContext,
} from "@/dnd/TransferDndProvider";
import { useUserID } from "@/hooks/userUserID";
import { useFileRecordCreateFromURL } from "@/pages/canvas/hooks/useFileRecordCreateFromURL";
import { DEFAULT_SECTION_COLOR } from "@/pages/canvas/nodes/shared/CanvasColorPalettes";
import { WIDGET_ALL_PRESET_MAP } from "@/pages/canvas/nodes/widgets";
import { buildEntityNodeAtFlowPosition } from "@/pages/canvas/utils/canvasEntityNodes";
import { layoutGridAroundCursor } from "@/pages/canvas/utils/canvasGridDropLayout";
import {
  DEFAULT_FILE_NODE_HEIGHT,
  DEFAULT_FILE_NODE_WIDTH,
  buildFileRecordEntityForCanvas,
} from "@/pages/canvas/utils/imageDimensions";
import { snapFlowPosition } from "@/pages/canvas/utils/quickAddCursor";
import { buildSectionRectAroundSlots } from "@/pages/canvas/utils/sectionDropLayout";
import {
  dataTransferItemsHaveDirectory,
  traverseDataTransferItems,
} from "@/utils/traverseDataTransferItems";
import type { FileRecord } from "@cohesive/api-client";
import { useDroppable } from "@dnd-kit/core";
import { useQueryClient } from "@tanstack/react-query";
import type { Node } from "@xyflow/react";
import { useReactFlow } from "@xyflow/react";
import { useCallback, useState } from "react";
import toast from "react-hot-toast";
import { v4 as uuidv4 } from "uuid";
import { useCanvas } from "../context/CanvasContext";
import { useCanvasRoute } from "../context/CanvasRouteContext";
import { useCanvasFileIngest } from "./useCanvasFileIngest";
import { useCanvasFolderIngest } from "./useCanvasFolderIngest";
import { useCanvasNodePlacement } from "./useCanvasNodePlacement";
import { useCanvasPermissions } from "./useCanvasPermissions";

export function useCanvasCanvasDropTarget() {
  const {
    getEntityNodeWidth,
    getEntityNodeHeight,
    getNextNodePosition,
    getNodeBounds,
  } = useCanvasNodePlacement();
  const { canEdit } = useCanvasPermissions();
  const reactFlowInstance = useReactFlow<Node>();
  const { canvasId } = useCanvasRoute();
  const ingestURL = useFileRecordCreateFromURL();
  const copyFileRecord = useCopyFileRecord(undefined);
  const { resolveCopies } = useFileConflictResolver();
  const { addNode, removeNode } = useCanvas();
  const { ingestFiles } = useCanvasFileIngest(canvasId);
  const { ingestFolderTree } = useCanvasFolderIngest(canvasId);
  const queryClient = useQueryClient();
  const currentUserId = useUserID();
  const [isDraggingFile, setIsDraggingFile] = useState(false);

  // Register the React Flow wrapper as a dnd-kit droppable so explorer
  // drags route through the unified dispatcher. The id encodes the
  // canvas id so multi-canvas tab setups don't collide.
  const droppable = useDroppable({
    id: canvasId ? droppableId.canvasBoard(canvasId) : "canvas-board:none",
    data: canvasId
      ? {
          surface: "canvas-board" as const,
          destination: { kind: "canvas" as const, canvasId },
        }
      : undefined,
    disabled: !canvasId || !canEdit,
  });

  // workspace files dropped on the board become references — a link row in the
  // canvas (the server renders it as its target), never a copy. Re-adding an
  // already-referenced file returns the existing row (server dedupe), so the
  // node just re-mints idempotently.
  const createReferenceOnCanvas = useCallback(
    async (fileRecordId: string): Promise<FileRecord> => {
      if (!canvasId) throw new Error("canvas id required for reference");
      // Lazy import to avoid a circular import with `@/api/api` at module
      // load (same reason as getWorkspaceFileRecords below).
      const { orgAPI } = await import("@/api/api");
      const res = await orgAPI.fileRecords.createReference(fileRecordId, {
        canvas_id: canvasId,
      });
      // Prime the detail cache the node about to be placed reads from — the
      // create response is the same row the GET returns, so the node never
      // races its own creation.
      queryClient.setQueryData(
        fileRecordKeys.detail(res.file_record.id),
        res.file_record,
      );
      void queryClient.invalidateQueries({
        queryKey: fileRecordKeys.canvas(canvasId),
      });
      return res.file_record;
    },
    [canvasId, queryClient],
  );

  // Interception's routine path (A3 + A2 back-to-back): promote the container
  // file into its workspace tree, then reference the promoted file here.
  // The origin reference the promotion leaves behind keeps the file visible
  // where it came from.
  const promoteToLibraryAndReference = useCallback(
    async (payload: ExplorerFileRecordCopyPayload): Promise<FileRecord> => {
      if (!canvasId) throw new Error("canvas id required for reference");
      const { orgAPI } = await import("@/api/api");
      const promoted = await orgAPI.fileRecords.addToWorkspace(
        payload.recordId,
      );
      if (payload.sourceCanvasId) {
        void queryClient.invalidateQueries({
          queryKey: fileRecordKeys.canvas(payload.sourceCanvasId),
        });
      }
      if (payload.sourceFlowSessionId) {
        void queryClient.invalidateQueries({
          queryKey: fileRecordKeys.flowSession(payload.sourceFlowSessionId),
        });
      }
      const workspaceId = promoted.file_record.workspace_id;
      if (workspaceId) {
        void queryClient.invalidateQueries({
          queryKey: fileRecordKeys.workspace(workspaceId),
        });
      }
      const res = await orgAPI.fileRecords.createReference(
        promoted.file_record.id,
        { canvas_id: canvasId },
      );
      queryClient.setQueryData(
        fileRecordKeys.detail(res.file_record.id),
        res.file_record,
      );
      void queryClient.invalidateQueries({
        queryKey: fileRecordKeys.canvas(canvasId),
      });
      return res.file_record;
    },
    [canvasId, queryClient],
  );

  // The board's workspace — drives the cross-workspace fork for workspace files.
  const { data: boardCanvas } = useCanvasById(canvasId ?? "");
  const boardWorkspaceId = boardCanvas?.workspace_id;

  // Native HTML5 channel keeps three payload families alive:
  //   - "Files"                    (OS desktop drops)
  //   - "application/widget-preset" (widget picker chips)
  //   - "text/uri-list"            (URL drops from browser address bar / bookmarks)
  // Entity picker rows now use dnd-kit (EntityDragRow), so the old
  // "application/explorer-entity[ies]" MIME types are no longer emitted.
  // dnd-kit pointer drags don't populate `dataTransfer.types` at all,
  // so the two systems remain mutually exclusive at the browser layer.
  const isHandledNative = (e: React.DragEvent): boolean =>
    e.dataTransfer.types.includes("Files") ||
    e.dataTransfer.types.includes("application/widget-preset") ||
    e.dataTransfer.types.includes("text/uri-list") ||
    e.dataTransfer.types.includes(WORKSPACE_FILE_DRAG_MIME) ||
    e.dataTransfer.types.includes(WORKSPACE_FOLDER_DRAG_MIME);

  const handleDragOver = useCallback((e: React.DragEvent) => {
    if (!isHandledNative(e)) return;
    e.preventDefault();
    e.stopPropagation();
  }, []);

  const handleDragEnter = useCallback(
    (e: React.DragEvent) => {
      if (!isHandledNative(e)) return;
      e.preventDefault();
      e.stopPropagation();
      if (canEdit) setIsDraggingFile(true);
    },
    [canEdit],
  );

  const handleDragLeave = useCallback((e: React.DragEvent) => {
    if (!isHandledNative(e)) return;
    e.preventDefault();
    e.stopPropagation();
    if (e.currentTarget === e.target) setIsDraggingFile(false);
  }, []);

  // OS file drop → the shared canvas ingestion pipeline, laid out in a tidy
  // grid centered on the cursor. Conflict resolution (Skip / Replace / Keep
  // both) runs inside `ingestFiles`.
  const uploadFilesIntoCanvasFlat = useCallback(
    async (files: File[], dropPosition: { x: number; y: number }) => {
      await ingestFiles(files, {
        placement: { strategy: "grid", at: dropPosition },
      });
    },
    [ingestFiles],
  );

  const handleDrop = useCallback(
    async (e: React.DragEvent) => {
      // Anything not handled natively (including dnd-kit pointer drags,
      // which set no `dataTransfer.types`) belongs to the unified
      // DndContext — we let it bubble untouched.
      if (!isHandledNative(e)) return;

      const isFiles = e.dataTransfer.types.includes("Files");
      const isWidget = e.dataTransfer.types.includes(
        "application/widget-preset",
      );
      const isUriList = e.dataTransfer.types.includes("text/uri-list");
      const isLibraryFile = e.dataTransfer.types.includes(
        WORKSPACE_FILE_DRAG_MIME,
      );
      const isLibraryFolder = e.dataTransfer.types.includes(
        WORKSPACE_FOLDER_DRAG_MIME,
      );

      e.preventDefault();
      e.stopPropagation();
      setIsDraggingFile(false);

      if (!canEdit) {
        toast(
          "You can only view this canvas — ask for edit access to add things.",
        );
        return;
      }
      if (!canvasId) return;

      // ── workspace file dragged from the docked Add-from-workspace picker ──
      // (native drag: the picker lives outside the dnd-kit provider). Mint a
      // reference and place its node at the cursor, same result as a rail
      // drag — re-drops de-duplicate server-side.
      if (isLibraryFile) {
        const fileRecordId = e.dataTransfer.getData(WORKSPACE_FILE_DRAG_MIME);
        if (!fileRecordId) return;
        const dropPos = reactFlowInstance.screenToFlowPosition({
          x: e.clientX,
          y: e.clientY,
        });
        void (async () => {
          try {
            const link = await createReferenceOnCanvas(fileRecordId);
            const { entity, nodeWidth, nodeHeight, blockMarkers } =
              buildFileRecordEntityForCanvas({
                record: link,
                contentType: link.content_type,
              });
            addNode({
              id: link.id,
              type: "entity",
              position: snapFlowPosition({
                x: dropPos.x - nodeWidth / 2,
                y: dropPos.y - nodeHeight / 2,
              }),
              width: nodeWidth,
              height: nodeHeight,
              data: { entity, ...blockMarkers },
            } as Node & Record<string, unknown>);
          } catch {
            toast.error("Failed to add from the workspace");
          }
        })();
        return;
      }

      // ── workspace folder dragged from the docked picker ──────────────
      // Same landing shape as a rail folder drag: a section titled after
      // the folder, one live reference per descendant file (subfolders
      // flatten — folder markers don't translate to canvas).
      if (isLibraryFolder) {
        const raw = e.dataTransfer.getData(WORKSPACE_FOLDER_DRAG_MIME);
        if (!raw) return;
        const dropPos = reactFlowInstance.screenToFlowPosition({
          x: e.clientX,
          y: e.clientY,
        });
        void (async () => {
          try {
            const payload = JSON.parse(raw) as WorkspaceFolderDragPayload;
            const wsRecords = await getWorkspaceFileRecords(
              queryClient,
              payload.workspaceId,
            );
            const folderPath = payload.path.endsWith("/")
              ? payload.path
              : `${payload.path}/`;
            const inFolder = wsRecords.filter(
              (r) =>
                r.path !== folderPath &&
                !r.path.endsWith("/") &&
                r.path.startsWith(folderPath),
            );
            const descendants = inFolder.filter((r) => r.kind !== "canvas");
            const skippedCanvases = inFolder.length - descendants.length;
            if (skippedCanvases > 0) {
              toast(
                descendants.length === 0
                  ? "This folder only contains canvases — canvases can't be placed on a canvas."
                  : `${skippedCanvases} ${skippedCanvases === 1 ? "canvas stays" : "canvases stay"} in the workspace — canvases can't be placed on a canvas.`,
              );
            }
            if (descendants.length === 0) {
              if (skippedCanvases === 0) toast("This folder has no files.");
              return;
            }

            const slots = descendants.map((record) => {
              const built = buildFileRecordEntityForCanvas({
                record,
                contentType: record.content_type,
              });
              return {
                record,
                entity: built.entity,
                nodeWidth: built.nodeWidth,
                nodeHeight: built.nodeHeight,
              };
            });
            const positions = layoutGridAroundCursor(slots, dropPos);
            const rect = buildSectionRectAroundSlots(
              slots.map((slot, i) => ({
                position: positions[i],
                width: slot.nodeWidth,
                height: slot.nodeHeight,
              })),
            );
            const sectionId = uuidv4();
            addNode({
              id: sectionId,
              type: "section",
              position: rect.position,
              width: rect.width,
              height: rect.height,
              zIndex: -1,
              data: {
                title: payload.name || "Section",
                sectionColor: DEFAULT_SECTION_COLOR,
                noFill: true,
                borderStyle: "solid",
                borderWidth: 2,
              },
            } as Parameters<typeof addNode>[0]);
            await placeCopiedFileSlots({
              slots: slots.map((slot, index) => ({
                op: undefined,
                mint: () => createReferenceOnCanvas(slot.record.id),
                nodeWidth: slot.nodeWidth,
                nodeHeight: slot.nodeHeight,
                title:
                  slot.record.path.split("/").filter(Boolean).pop() ?? "File",
                absPosition: positions[index],
              })),
              parentAbsPos: rect.position,
              parentSectionId: sectionId,
              addNode,
              removeNode,
              copyFileRecord,
            });
          } catch {
            toast.error("Failed to add folder from the workspace");
          }
        })();
        return;
      }

      // ── Widget preset drag-and-drop from the widget picker ────────
      if (isWidget) {
        const widgetPresetId = e.dataTransfer.getData(
          "application/widget-preset",
        );
        if (!widgetPresetId) return;
        const preset = WIDGET_ALL_PRESET_MAP.get(widgetPresetId);
        if (!preset) return;
        const dropPos = reactFlowInstance.screenToFlowPosition({
          x: e.clientX,
          y: e.clientY,
        });
        addNode({
          id: uuidv4(),
          type: "widgetNode",
          position: snapFlowPosition({ x: dropPos.x, y: dropPos.y }),
          width: preset.defaultWidth,
          // Same rule as handleQuickPlaceWidget: only fill-mode widgets
          // need an explicit height up-front. Scale-mode widgets (Timer,
          // Music) are measured by React Flow / ReactWidgetShell first.
          ...(preset.resizable?.scaleContent === false
            ? { height: preset.defaultHeight }
            : {}),
          ...(preset.zIndex !== undefined && { zIndex: preset.zIndex }),
          // React Flow node-level flags (selectable / deletable / draggable /
          // focusable). See WidgetPreset.nodeProps.
          ...(preset.nodeProps ?? {}),
          data: {
            ...(preset.component ? {} : { html: preset.html }),
            label: preset.label,
            presetId: preset.id,
            ...(preset.initialData ?? {}),
          },
        } as Parameters<typeof addNode>[0]);
        return;
      }

      // ── URL drop (text/uri-list) ────────────────────────────────────
      if (isUriList && !isFiles) {
        const uriList = e.dataTransfer.getData("text/uri-list");
        const dropPos = reactFlowInstance.screenToFlowPosition({
          x: e.clientX,
          y: e.clientY,
        });
        const urls = uriList
          .split(/\r?\n/)
          .map((u) => u.trim())
          .filter(
            (u) =>
              u &&
              !u.startsWith("#") &&
              (u.startsWith("http://") || u.startsWith("https://")),
          );
        urls.forEach((url, i) => {
          ingestURL({
            sourceUrl: url,
            position: snapFlowPosition({
              x: dropPos.x + i * 40,
              y: dropPos.y + i * 40,
            }),
          });
        });
        return;
      }

      // ── OS file drop: upload via shared queue + ghost-then-real swap ──
      if (!isFiles) return;

      // Capture DataTransferItem references SYNCHRONOUSLY before any
      // await — `webkitGetAsEntry` references invalidate as soon as
      // the handler yields.
      const itemsSnapshot: DataTransferItem[] = e.dataTransfer.items
        ? Array.from(e.dataTransfer.items)
        : [];

      const dropPosition = reactFlowInstance.screenToFlowPosition({
        x: e.clientX,
        y: e.clientY,
      });

      const hasDirectory = dataTransferItemsHaveDirectory(itemsSnapshot);

      // Loose-files-only path (no folders) — keep today's `dataTransfer.files`
      // flow unchanged so non-folder drops are bit-identical to before.
      if (!hasDirectory) {
        const files = Array.from(e.dataTransfer.files);
        if (files.length === 0) return;
        await uploadFilesIntoCanvasFlat(files, dropPosition);
        return;
      }

      // Mixed / folder path — walk the tree, route each top-level folder
      // into its own section and loose top-level files into the flat grid.
      const tree = await traverseDataTransferItems(itemsSnapshot);
      await ingestFolderTree(tree, dropPosition);
    },
    [
      canvasId,
      addNode,
      reactFlowInstance,
      canEdit,
      uploadFilesIntoCanvasFlat,
      ingestFolderTree,
      getEntityNodeWidth,
      getEntityNodeHeight,
      createReferenceOnCanvas,
      queryClient,
      removeNode,
      copyFileRecord,
    ],
  );

  // Board-drop handler invoked by the dispatcher when an explorer drag
  // is released over the `canvas-board:<canvasId>` droppable. Mirrors
  // the prior in-line copy branch in `handleDrop`.
  const onCanvasBoardDrop = useCallback(
    async ({
      screenX,
      screenY,
      payloads,
      parentSectionId,
      chatSession,
      entities,
      useViewportPlacement,
    }: CanvasBoardDropContext) => {
      if (!canvasId) return false;
      if (!canEdit) {
        toast(
          "You can only view this canvas — ask for edit access to add things.",
        );
        return false;
      }

      const sectionAnchor = parentSectionId
        ? getSectionPlacementAnchor(reactFlowInstance, parentSectionId)
        : null;

      const resolveDropPosition = (
        slotWidth = DEFAULT_FILE_NODE_WIDTH,
        slotHeight = DEFAULT_FILE_NODE_HEIGHT,
      ) => {
        if (sectionAnchor) return sectionAnchor.interiorCentroidAbs;
        if (useViewportPlacement) {
          return getNextNodePosition(slotWidth, slotHeight);
        }
        return reactFlowInstance.screenToFlowPosition({
          x: screenX,
          y: screenY,
        });
      };

      // ── Chat session placement ───────────────────────────────────
      // Mirrors `useQuickChat`'s `chatSession` placement: mints a
      // fresh node id (so the session can be referenced by multiple
      // placements if dragged again later) and fills `data` with the
      // same shape `ChatSessionNode` / `ChatPanel` expect. No new
      // flow session is created — the dropped node points at the
      // existing `flowSessionId` so the chat history opens unchanged.
      if (chatSession) {
        const absPos = resolveDropPosition();
        const dropPos = snapFlowPosition(
          sectionAnchor
            ? toSectionRelativePosition(absPos, sectionAnchor.sectionAbsPos)
            : absPos,
        );
        const firstMessage = chatSession.name.slice(0, 80) || "Canvas Agent";
        addNode({
          id: uuidv4(),
          type: "chatSession",
          position: dropPos,
          ...(parentSectionId ? { parentId: parentSectionId } : {}),
          data: {
            flowSessionId: chatSession.flowSessionId,
            status: "active",
            firstMessage,
            userId: currentUserId,
          },
        } as Parameters<typeof addNode>[0]);
        if (payloads.length === 0) return true;
      }

      // ── Entity picker drop (from CanvasInsertMenu) ──
      // Mints a fresh entity node for each entity — no copy/upload needed.
      // Grid layout and grid-snap mirrors the legacy HTML5 entity path.
      if (entities && entities.length > 0) {
        const entityDropPosition = resolveDropPosition(
          getEntityNodeWidth(entities[0]),
          getEntityNodeHeight(entities[0]),
        );
        const entitySlots = entities.map((ent) => ({
          entity: ent,
          nodeWidth: getEntityNodeWidth(ent),
          nodeHeight: getEntityNodeHeight(ent),
        }));
        const entityPositions = layoutGridAroundCursor(
          entitySlots,
          entityDropPosition,
        );
        entitySlots.forEach((slot, i) => {
          const absPos = entityPositions[i];
          const position = sectionAnchor
            ? toSectionRelativePosition(absPos, sectionAnchor.sectionAbsPos)
            : absPos;
          const newNode = buildEntityNodeAtFlowPosition(slot.entity, position, {
            getEntityNodeWidth,
            getEntityNodeHeight,
          });
          addNode({
            ...newNode,
            ...(parentSectionId ? { parentId: parentSectionId } : {}),
          } as Parameters<typeof addNode>[0]);
        });
        return true;
      }

      // Split payloads into four buckets:
      //   - placeExisting: same canvas, file is "stored" (no node yet) —
      //     mint a node for the existing record, no copy / resolver.
      //   - alreadyPlaced: same canvas, already has a node — silently
      //     skip (the classifier shouldn't have routed these to us, but
      //     keep the guard).
      //   - workspaceFolders: workspace-source folder payloads — copy
      //     descendant files onto the canvas in a grid.
      //   - externalCopies: cross-canvas / workspace files — the legacy
      //     copy + conflict-resolver path.
      const isSameCanvas = (p: (typeof payloads)[number]) =>
        p.sourceClass === "canvas" && p.sourceCanvasId === canvasId;
      const placeExisting = payloads.filter(
        (p) => isSameCanvas(p) && p.sourcePlaced === false,
      );
      const workspaceFolders = payloads.filter(
        (p) =>
          p.isFolder && p.sourceClass === "workspace" && !!p.sourceWorkspaceId,
      );
      // Workspace-class files fork at the door, same as picker
      // picks: same-workspace drops ask reference (default) or copy;
      // cross-workspace drops ask copy (default) or a labeled live
      // reference (the 2×2's separate-buildings rule).
      const libraryFiles = payloads.filter(
        (p) => !isSameCanvas(p) && !p.isFolder && p.sourceClass === "workspace",
      );
      const sameWorkspaceLibrary = libraryFiles.filter(
        (p) =>
          !boardWorkspaceId ||
          !p.sourceWorkspaceId ||
          p.sourceWorkspaceId === boardWorkspaceId,
      );
      const crossWorkspaceLibrary = libraryFiles.filter(
        (p) => !sameWorkspaceLibrary.includes(p),
      );
      // Files living on another canvas / in a chat hit the interception:
      // promote-and-reference (routine) or an explicit independent copy.
      const containerFiles = payloads.filter(
        (p) => !isSameCanvas(p) && !p.isFolder && p.sourceClass !== "workspace",
      );
      if (
        placeExisting.length === 0 &&
        libraryFiles.length === 0 &&
        containerFiles.length === 0 &&
        workspaceFolders.length === 0
      ) {
        return true;
      }

      const libraryReferences: typeof payloads = [];
      const externalCopies: typeof payloads = [];
      const promoteAndReference: typeof payloads = [];

      if (sameWorkspaceLibrary.length > 0) {
        const choice = await runWorkspaceAddModeWorkflow({
          count: sameWorkspaceLibrary.length,
          fileName: sameWorkspaceLibrary[0]?.name,
          destination: "canvas",
        });
        if (choice === "reference") {
          libraryReferences.push(...sameWorkspaceLibrary);
        } else if (choice === "copy") {
          externalCopies.push(...sameWorkspaceLibrary);
        }
      }

      if (crossWorkspaceLibrary.length > 0) {
        const choice = await runCrossWorkspaceFileDropWorkflow({
          count: crossWorkspaceLibrary.length,
          fileName: crossWorkspaceLibrary[0]?.name,
          destination: "canvas",
        });
        if (choice === "copy") {
          externalCopies.push(...crossWorkspaceLibrary);
        } else if (choice === "reference") {
          libraryReferences.push(...crossWorkspaceLibrary);
        }
      }

      if (containerFiles.length > 0) {
        const choice = await runContainerFileDropWorkflow({
          count: containerFiles.length,
          fileName: containerFiles[0]?.name,
          destination: "canvas",
        });
        if (choice === "workspace") {
          promoteAndReference.push(...containerFiles);
        } else if (choice === "copy") {
          externalCopies.push(...containerFiles);
        }
      }

      // When the drop targets a specific section we resolve the drop
      // position from the section's interior (flow coords) rather than
      // from the cursor — the cursor is in the explorer pane DOM and
      // its mapped flow position is unrelated to where we want the
      // new nodes to land.
      const dropPosition = resolveDropPosition();

      // ── Place existing stored files ─────────────────────────────────
      // Just `addNode` — the FileRecord already lives in the canvas's
      // file volume, so no API calls are needed. Build the entity
      // directly from payload metadata (including source width/height for
      // image aspect). Multi-drag lays out in a square-ish grid centered on
      // the cursor (same helper used by the OS-file drop path) so a
      // batch of files doesn't all stack on the same coordinate.
      const placeSlots = placeExisting.map((payload) => {
        const stubRecord = {
          id: payload.recordId,
          path: payload.sourcePath,
          content_type: payload.contentType,
          ...(payload.width != null ? { width: payload.width } : {}),
          ...(payload.height != null ? { height: payload.height } : {}),
          ...(payload.kind ? { kind: payload.kind } : {}),
          ...(payload.targetFileRecordId
            ? { target_file_record_id: payload.targetFileRecordId }
            : {}),
        } as Parameters<typeof buildFileRecordEntityForCanvas>[0]["record"];
        const built = buildFileRecordEntityForCanvas({
          record: stubRecord,
          contentType: payload.contentType,
        });
        return {
          payload,
          entity: built.entity,
          nodeWidth: built.nodeWidth,
          nodeHeight: built.nodeHeight,
        };
      });
      if (placeSlots.length > 0) {
        const placePositions = layoutGridAroundCursor(placeSlots, dropPosition);
        placeSlots.forEach((slot, i) => {
          const absPos = placePositions[i];
          const position = sectionAnchor
            ? toSectionRelativePosition(absPos, sectionAnchor.sectionAbsPos)
            : absPos;
          addNode({
            id: slot.payload.recordId,
            type: "entity",
            position,
            ...(sectionAnchor && parentSectionId
              ? { parentId: parentSectionId }
              : {}),
            width: slot.nodeWidth,
            height: slot.nodeHeight,
            data: { entity: slot.entity },
          } as Parameters<typeof addNode>[0]);
        });
      }

      if (workspaceFolders.length > 0) {
        let folderOffsetX = 0;
        for (const folderPayload of workspaceFolders) {
          const wsId = folderPayload.sourceWorkspaceId!;
          const wsRecords = await getWorkspaceFileRecords(queryClient, wsId);
          const { payloads: descendantPayloads, skippedCanvases } =
            collectFolderDescendantPayloads(wsRecords, folderPayload);
          if (skippedCanvases > 0) {
            toast(
              descendantPayloads.length === 0
                ? "This folder only contains canvases — canvases can't be placed on a canvas."
                : `${skippedCanvases} ${skippedCanvases === 1 ? "canvas stays" : "canvases stay"} in the workspace — canvases can't be placed on a canvas.`,
            );
          }
          if (descendantPayloads.length === 0) continue;

          const folderPlacementReservations = useViewportPlacement
            ? reserveViewportPlacementSlots({
                count: descendantPayloads.length,
                getNextNodePosition,
                addNode,
              })
            : [];

          let folderReservationsReleased = false;
          const releaseFolderReservations = () => {
            if (folderReservationsReleased) return;
            folderReservationsReleased = true;
            releaseViewportPlacementReservations(
              folderPlacementReservations,
              removeNode,
            );
          };

          try {
            // A workspace folder lands as a section of references — one live
            // reference per descendant file, never copies. No conflict
            // resolver: the reference slot names are invisible and the server
            // dedupes re-adds per container.
            const folderSlots = descendantPayloads.map((payload) => {
              const stubRecord = {
                id: payload.recordId,
                path: payload.sourcePath,
                content_type: payload.contentType,
                ...(payload.width != null ? { width: payload.width } : {}),
                ...(payload.height != null ? { height: payload.height } : {}),
              } as Parameters<
                typeof buildFileRecordEntityForCanvas
              >[0]["record"];
              const built = buildFileRecordEntityForCanvas({
                record: stubRecord,
                contentType: payload.contentType,
              });
              return {
                entity: built.entity,
                nodeWidth: built.nodeWidth,
                nodeHeight: built.nodeHeight,
                payload,
              };
            });

            const firstFolderSlot = folderSlots[0];
            const folderGridAnchor =
              useViewportPlacement && folderPlacementReservations.length > 0
                ? folderPlacementReservations[0]!.absPosition
                : resolveCopyGridAnchor({
                    useViewportPlacement,
                    preferred: {
                      x: dropPosition.x + folderOffsetX,
                      y: dropPosition.y,
                    },
                    slotWidth:
                      firstFolderSlot?.nodeWidth ?? DEFAULT_FILE_NODE_WIDTH,
                    slotHeight:
                      firstFolderSlot?.nodeHeight ?? DEFAULT_FILE_NODE_HEIGHT,
                    getNextNodePosition,
                    getNodeBounds,
                    nodes: reactFlowInstance.getNodes(),
                  });
            releaseFolderReservations();
            const folderPositions = layoutGridAroundCursor(
              folderSlots,
              folderGridAnchor,
            );

            let folderSectionId: string | null = null;
            let childParentAbsPos = sectionAnchor?.sectionAbsPos ?? null;
            if (!parentSectionId) {
              const rect = buildSectionRectAroundSlots(
                folderSlots.map((slot, i) => ({
                  position: folderPositions[i],
                  width: slot.nodeWidth,
                  height: slot.nodeHeight,
                })),
              );
              folderSectionId = uuidv4();
              childParentAbsPos = rect.position;
              addNode({
                id: folderSectionId,
                type: "section",
                position: rect.position,
                width: rect.width,
                height: rect.height,
                zIndex: -1,
                data: {
                  title: folderPayload.name || "Section",
                  sectionColor: DEFAULT_SECTION_COLOR,
                  noFill: true,
                  borderStyle: "solid",
                  borderWidth: 2,
                },
              } as Parameters<typeof addNode>[0]);
            }
            const childParentId = folderSectionId ?? parentSectionId ?? null;

            await placeCopiedFileSlots({
              slots: folderSlots.map((slot, index) => ({
                op: undefined,
                mint: () => createReferenceOnCanvas(slot.payload.recordId),
                nodeWidth: slot.nodeWidth,
                nodeHeight: slot.nodeHeight,
                title: slot.payload.name ?? "File",
                absPosition: folderPositions[index],
              })),
              parentAbsPos: childParentAbsPos,
              parentSectionId: childParentId ?? undefined,
              addNode,
              removeNode,
              copyFileRecord,
            });
            folderOffsetX += 700;
          } catch {
            toast.error("Failed to add folder onto canvas");
          } finally {
            releaseFolderReservations();
          }
        }
      }

      if (
        externalCopies.length === 0 &&
        libraryReferences.length === 0 &&
        promoteAndReference.length === 0
      ) {
        return true;
      }

      // ── Cross-scope copies + workspace references ─────────────────────
      const copyPlacementReservations = useViewportPlacement
        ? reserveViewportPlacementSlots({
            count:
              externalCopies.length +
              libraryReferences.length +
              promoteAndReference.length,
            getNextNodePosition,
            addNode,
          })
        : [];

      let copyReservationsReleased = false;
      const releaseCopyReservations = () => {
        if (copyReservationsReleased) return;
        copyReservationsReleased = true;
        releaseViewportPlacementReservations(
          copyPlacementReservations,
          removeNode,
        );
      };

      let copyGridAnchor: { x: number; y: number };
      try {
        // resolveCopies may return fewer ops than payloads (user skipped
        // some, merged folders, etc.), so positions are computed off the
        // resolved op list.
        const workspaceSourceIds = [
          ...new Set(
            externalCopies
              .filter(
                (p) => p.sourceClass === "workspace" && p.sourceWorkspaceId,
              )
              .map((p) => p.sourceWorkspaceId!),
          ),
        ];
        const sourceRecords = (
          await Promise.all(
            workspaceSourceIds.map((wsId) =>
              getWorkspaceFileRecords(queryClient, wsId),
            ),
          )
        ).flat();

        const ops = await resolveCopies({
          copies: externalCopies.map((payload) => ({
            fileRecordId: payload.recordId,
            sourceName: payload.name,
            isFolder: payload.isFolder,
            source: {
              class: payload.sourceClass,
              workspace_id: payload.sourceWorkspaceId,
              canvas_id: payload.sourceCanvasId,
              flow_session_id: payload.sourceFlowSessionId,
              path: payload.sourcePath,
              is_folder: payload.isFolder || undefined,
            },
            destination: {
              components: { class: "canvas", canvas_id: canvasId },
              parentPath: "",
            },
          })),
          sourceRecords,
        });
        if (
          (!ops || ops.length === 0) &&
          libraryReferences.length === 0 &&
          promoteAndReference.length === 0
        ) {
          return;
        }

        // Build a recordId → payload map so each op can look up its
        // source payload's content_type / path for size prediction.
        const payloadById = new Map(
          [...externalCopies, ...libraryReferences, ...promoteAndReference].map(
            (p) => [p.recordId, p],
          ),
        );

        const buildSlotSizing = (
          recordId: string,
        ): { contentType: string; nodeWidth: number; nodeHeight: number } => {
          const payload = payloadById.get(recordId);
          const contentType =
            payload?.contentType ?? "application/octet-stream";
          const stubRecord = {
            id: recordId,
            path: payload?.sourcePath ?? "",
            content_type: contentType,
            ...(payload?.width != null ? { width: payload.width } : {}),
            ...(payload?.height != null ? { height: payload.height } : {}),
          } as Parameters<typeof buildFileRecordEntityForCanvas>[0]["record"];
          const built = buildFileRecordEntityForCanvas({
            record: stubRecord,
            contentType,
          });
          return {
            contentType,
            nodeWidth: built.nodeWidth,
            nodeHeight: built.nodeHeight,
          };
        };

        // References first (the routine path), then promotions, then copies.
        const referenceSlots = libraryReferences.map((payload) => ({
          op: undefined,
          mint: () => createReferenceOnCanvas(payload.recordId),
          recordId: payload.recordId,
          ...buildSlotSizing(payload.recordId),
        }));
        const promoteSlots = promoteAndReference.map((payload) => ({
          op: undefined,
          mint: () => promoteToLibraryAndReference(payload),
          recordId: payload.recordId,
          ...buildSlotSizing(payload.recordId),
        }));
        const copySlots = (ops ?? []).map((op) => ({
          op,
          mint: undefined,
          recordId: op.sourceFileRecordId,
          ...buildSlotSizing(op.sourceFileRecordId),
        }));
        const allSlots = [...referenceSlots, ...promoteSlots, ...copySlots];
        const copyOriginY =
          placeSlots.length > 0
            ? dropPosition.y +
              Math.max(...placeSlots.map((s) => s.nodeHeight)) +
              40
            : dropPosition.y;
        const preferredCopyAnchor = { x: dropPosition.x, y: copyOriginY };
        const firstCopySlot = allSlots[0];
        copyGridAnchor =
          useViewportPlacement && copyPlacementReservations.length > 0
            ? copyPlacementReservations[0]!.absPosition
            : resolveCopyGridAnchor({
                useViewportPlacement,
                preferred: preferredCopyAnchor,
                slotWidth: firstCopySlot?.nodeWidth ?? DEFAULT_FILE_NODE_WIDTH,
                slotHeight:
                  firstCopySlot?.nodeHeight ?? DEFAULT_FILE_NODE_HEIGHT,
                getNextNodePosition,
                getNodeBounds,
                nodes: reactFlowInstance.getNodes(),
              });

        releaseCopyReservations();
        const copyPositions = layoutGridAroundCursor(allSlots, copyGridAnchor);

        await placeCopiedFileSlots({
          slots: allSlots.map((slot, index) => ({
            op: slot.op,
            mint: slot.mint,
            nodeWidth: slot.nodeWidth,
            nodeHeight: slot.nodeHeight,
            title: payloadById.get(slot.recordId)?.name ?? "File",
            absPosition: copyPositions[index],
          })),
          parentAbsPos: sectionAnchor?.sectionAbsPos ?? null,
          parentSectionId,
          addNode,
          removeNode,
          copyFileRecord,
        });
      } catch {
        toast.error("Failed to add file onto canvas");
      } finally {
        releaseCopyReservations();
      }

      return true;
    },
    [
      canvasId,
      canEdit,
      currentUserId,
      reactFlowInstance,
      resolveCopies,
      copyFileRecord,
      addNode,
      removeNode,
      queryClient,
      getEntityNodeWidth,
      getEntityNodeHeight,
      getNextNodePosition,
      getNodeBounds,
      createReferenceOnCanvas,
      promoteToLibraryAndReference,
      boardWorkspaceId,
    ],
  );

  useRegisterCanvasBoardDropHandler(onCanvasBoardDrop);

  return {
    isDraggingFile: isDraggingFile || droppable.isOver,
    /** Attach to the React Flow wrapper so dnd-kit can hit-test it. */
    setBoardRef: droppable.setNodeRef,
    handleDragOver,
    handleDragEnter,
    handleDragLeave,
    handleDrop,
  };
}

const SECTION_INTERIOR_INSET_X = 24;
const SECTION_INTERIOR_INSET_Y = 48;

type SectionPlacementAnchor = {
  sectionAbsPos: { x: number; y: number };
  interiorCentroidAbs: { x: number; y: number };
};

function getSectionPlacementAnchor(
  reactFlow: ReturnType<typeof useReactFlow<Node>>,
  sectionId: string,
): SectionPlacementAnchor | null {
  const internal = reactFlow.getInternalNode(sectionId);
  const parentNode = reactFlow.getNode(sectionId);
  if (!parentNode || parentNode.type !== "section") return null;

  const sectionAbsPos =
    internal?.internals.positionAbsolute ?? parentNode.position;
  const sectionWidth = (parentNode.width as number | undefined) ?? 0;
  const sectionHeight = (parentNode.height as number | undefined) ?? 0;

  return {
    sectionAbsPos,
    interiorCentroidAbs: {
      x:
        sectionAbsPos.x +
        SECTION_INTERIOR_INSET_X +
        Math.max(0, sectionWidth - SECTION_INTERIOR_INSET_X * 2) / 2,
      y:
        sectionAbsPos.y +
        SECTION_INTERIOR_INSET_Y +
        Math.max(
          0,
          sectionHeight - SECTION_INTERIOR_INSET_Y - SECTION_INTERIOR_INSET_X,
        ) /
          2,
    },
  };
}

function toSectionRelativePosition(
  absPos: { x: number; y: number },
  sectionAbsPos: { x: number; y: number },
): { x: number; y: number } {
  return {
    x: absPos.x - sectionAbsPos.x,
    y: absPos.y - sectionAbsPos.y,
  };
}

type NodeRect = { left: number; top: number; right: number; bottom: number };

function rectsOverlap(a: NodeRect, b: NodeRect): boolean {
  return !(
    a.right <= b.left ||
    a.left >= b.right ||
    a.bottom <= b.top ||
    a.top >= b.bottom
  );
}

function nodeRectOverlapsAny(
  nodes: Node[],
  getNodeBounds: (node: Node) => NodeRect,
  left: number,
  top: number,
  width: number,
  height: number,
): boolean {
  const candidate = { left, top, right: left + width, bottom: top + height };
  return nodes.some((node) => rectsOverlap(candidate, getNodeBounds(node)));
}

/** Pick a grid anchor — viewport column slot, or cursor unless it overlaps. */
function resolveCopyGridAnchor(params: {
  useViewportPlacement?: boolean;
  preferred: { x: number; y: number };
  slotWidth: number;
  slotHeight: number;
  getNextNodePosition: (w?: number, h?: number) => { x: number; y: number };
  getNodeBounds: (node: Node) => NodeRect;
  nodes: Node[];
}): { x: number; y: number } {
  const {
    useViewportPlacement,
    preferred,
    slotWidth,
    slotHeight,
    getNextNodePosition,
    getNodeBounds,
    nodes,
  } = params;
  if (useViewportPlacement) {
    return getNextNodePosition(slotWidth, slotHeight);
  }
  if (
    nodeRectOverlapsAny(
      nodes,
      getNodeBounds,
      preferred.x,
      preferred.y,
      slotWidth,
      slotHeight,
    )
  ) {
    return getNextNodePosition(slotWidth, slotHeight);
  }
  return preferred;
}

function flowPositionFromAbs(
  absPos: { x: number; y: number },
  parentAbsPos: { x: number; y: number } | null,
): { x: number; y: number } {
  return parentAbsPos
    ? toSectionRelativePosition(absPos, parentAbsPos)
    : absPos;
}

type ViewportPlacementReservation = {
  nodeId: string;
  absPosition: { x: number; y: number };
};

/**
 * Mint loading ghosts before async conflict resolution so concurrent
 * viewport drops each reserve distinct slots via `getNextNodePosition`.
 */
function reserveViewportPlacementSlots(params: {
  count: number;
  getNextNodePosition: (w?: number, h?: number) => { x: number; y: number };
  addNode: CanvasAddNode;
  slotWidth?: number;
  slotHeight?: number;
}): ViewportPlacementReservation[] {
  const {
    count,
    getNextNodePosition,
    addNode,
    slotWidth = DEFAULT_FILE_NODE_WIDTH,
    slotHeight = DEFAULT_FILE_NODE_HEIGHT,
  } = params;
  const reservations: ViewportPlacementReservation[] = [];
  for (let i = 0; i < count; i++) {
    const absPosition = getNextNodePosition(slotWidth, slotHeight);
    const nodeId = uuidv4();
    addNode({
      id: nodeId,
      type: "entity",
      position: absPosition,
      width: slotWidth,
      height: slotHeight,
      data: {
        loading: true,
        entity: {
          type: "file_record",
          base: "file_record",
          platform: "cohesive",
          data: { id: "", title: "File" },
        },
      },
    } as Node & Record<string, unknown>);
    reservations.push({ nodeId, absPosition });
  }
  return reservations;
}

function releaseViewportPlacementReservations(
  reservations: ViewportPlacementReservation[],
  removeNode: (id: string) => void,
): void {
  for (const reservation of reservations) {
    removeNode(reservation.nodeId);
  }
}

type CopyFileSlotPlacement = {
  /** Copy operation — present on the legacy copy path. */
  op?: ResolvedCopyOperation;
  /**
   * File system v2 reference path: mints the record (a link row) instead of
   * copying. Exactly one of `op` / `mint` is set per slot.
   */
  mint?: () => Promise<FileRecord>;
  nodeWidth: number;
  nodeHeight: number;
  title: string;
  absPosition: { x: number; y: number };
};

type CanvasAddNode = (node: Node & Record<string, unknown>) => void;

/**
 * Mint loading ghosts at resolved positions immediately so rapid sequential
 * drops see reserved slots via `getNextNodePosition`, then swap in real nodes
 * after each copy API call completes.
 */
async function placeCopiedFileSlots(params: {
  slots: CopyFileSlotPlacement[];
  parentAbsPos: { x: number; y: number } | null;
  parentSectionId?: string;
  addNode: CanvasAddNode;
  removeNode: (id: string) => void;
  copyFileRecord: {
    mutateAsync: (args: {
      fileRecordId: string;
      payload: ResolvedCopyOperation["payload"];
    }) => Promise<FileRecord>;
  };
}): Promise<void> {
  const {
    slots,
    parentAbsPos,
    parentSectionId,
    addNode,
    removeNode,
    copyFileRecord,
  } = params;

  const ghosts = slots.map((slot) => ({
    ...slot,
    nodeId: uuidv4(),
  }));

  for (const ghost of ghosts) {
    addNode({
      id: ghost.nodeId,
      type: "entity",
      position: flowPositionFromAbs(ghost.absPosition, parentAbsPos),
      width: ghost.nodeWidth,
      height: ghost.nodeHeight,
      ...(parentSectionId ? { parentId: parentSectionId } : {}),
      data: {
        loading: true,
        entity: {
          type: "file_record",
          base: "file_record",
          platform: "cohesive",
          data: { id: "", title: ghost.title },
        },
      },
    } as Node & Record<string, unknown>);
  }

  await Promise.allSettled(
    ghosts.map(async (ghost) => {
      try {
        let newRecord: FileRecord;
        if (ghost.mint) {
          newRecord = await ghost.mint();
        } else if (ghost.op) {
          newRecord = await copyFileRecord.mutateAsync({
            fileRecordId: ghost.op.sourceFileRecordId,
            payload: ghost.op.payload,
          });
        } else {
          removeNode(ghost.nodeId);
          return;
        }
        removeNode(ghost.nodeId);
        const { entity, nodeWidth, nodeHeight, blockMarkers } =
          buildFileRecordEntityForCanvas({
            record: newRecord,
            contentType: newRecord.content_type,
          });
        addNode({
          id: newRecord.id,
          type: "entity",
          position: flowPositionFromAbs(ghost.absPosition, parentAbsPos),
          ...(parentSectionId ? { parentId: parentSectionId } : {}),
          width: nodeWidth,
          height: nodeHeight,
          // A block reference renders live from the first frame — the
          // record's display facts ride the node (paint never reads the
          // record).
          data: { entity, ...blockMarkers },
        } as Node & Record<string, unknown>);
      } catch {
        removeNode(ghost.nodeId);
        toast.error("Failed to copy file onto canvas");
      }
    }),
  );
}

/**
 * Read the workspace's file records from the React Query cache (the
 * sidebar / workspace pages already populate it). Falls back to a
 * direct list call if the cache is cold so a fresh-load drop still
 * works.
 */
async function getWorkspaceFileRecords(
  queryClient: ReturnType<typeof useQueryClient>,
  workspaceId: string,
): Promise<FileRecord[]> {
  // Lazy import: `@/api/fileRecords` pulls in `@/api/api`, a cycle at load.
  const { workspaceTreeQuery } = await import("@/api/fileRecords");
  return queryClient.fetchQuery({
    ...workspaceTreeQuery(workspaceId),
    staleTime: Infinity,
  });
}

/**
 * Walk the workspace's flat record list to collect every descendant
 * FILE of the dropped folder, flattened (subfolder structure ignored).
 * Canvas rows are excluded — a canvas can't be placed on a canvas — and
 * counted so the caller can say they stayed behind. Returns one
 * ExplorerFileRecordCopyPayload per descendant file.
 */
function collectFolderDescendantPayloads(
  workspaceRecords: FileRecord[],
  folderPayload: ExplorerFileRecordCopyPayload,
): { payloads: ExplorerFileRecordCopyPayload[]; skippedCanvases: number } {
  // The folder marker's path ends with "/" by convention; descendant
  // file paths start with that prefix.
  const folderPath = folderPayload.sourcePath.endsWith("/")
    ? folderPayload.sourcePath
    : `${folderPayload.sourcePath}/`;
  const wsId = folderPayload.sourceWorkspaceId;
  if (!wsId) return { payloads: [], skippedCanvases: 0 };

  let skippedCanvases = 0;
  const payloads = workspaceRecords
    .filter((r) => {
      if (r.path === folderPath) return false; // skip the folder marker itself
      if (r.path.endsWith("/")) return false; // flatten — subfolder markers don't translate to canvas
      if (!r.path.startsWith(folderPath)) return false;
      if (r.kind === "canvas") {
        skippedCanvases += 1;
        return false;
      }
      return true;
    })
    .map((r) => buildCopyPayloadFromRecord(r, { workspaceId: wsId }));
  return { payloads, skippedCanvases };
}
